import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../hooks/useCurrency';

const PAYMENT_METHODS = [
  { src: 'https://digicodestv.com/assets/visa-mastercard-7a0aLWVP.png', label: 'Visa/Mastercard', discount: '-10%' },
  { src: 'https://digicodestv.com/assets/virement-bancaire-DKiGfR4X.png', label: 'Virement Bancaire' },
  { src: 'https://digicodestv.com/assets/wafacash-DCDiJGic.png', label: 'Wafacash' },
  { src: 'https://digicodestv.com/assets/cashplus-9P0DtsOu.png', label: 'CashPlus' },
  { src: 'https://digicodestv.com/assets/banques-DTUW1yW7.png', label: 'Banques' },
  { src: 'https://digicodestv.com/assets/paypal-RPN4DA3m.png', label: 'PayPal' },
  { src: 'https://digicodestv.com/assets/remitly-DRdtHywJ.png', label: 'Remitly' },
  { src: 'https://digicodestv.com/assets/western-union-DbEP3cji.png', label: 'Western Union' },
  { src: 'https://digicodestv.com/assets/binance-0eNF3tR-.png', label: 'Binance' },
  { src: 'https://digicodestv.com/assets/compte-europe-B8Igz480.png', label: 'Compte Europe' },
];

function PremiumPlan() {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const premiumStats = [
    { valKey: 'pricing.s1_val', subKey: 'pricing.s1_sub', icon: 'ri-tv-line' },
    { valKey: 'pricing.s2_val', subKey: 'pricing.s2_sub', icon: 'ri-movie-2-line' },
    { valKey: 'pricing.s3_val', subKey: 'pricing.s3_sub', icon: 'ri-apps-line' },
    { valKey: 'pricing.s4_val', subKey: 'pricing.s4_sub', icon: 'ri-wifi-line' },
  ];

  const premiumFeatures = [
    'pricing.f1', 'pricing.f2', 'pricing.f3', 'pricing.f4',
    'pricing.f5', 'pricing.f6', 'pricing.f7', 'pricing.f8',
  ];

  type CurrencyKey = 'USD' | 'EUR' | 'GBP';
  const formatPrice = (n: number) => (n % 1 === 0 ? n.toString() : n.toFixed(2));

  const tiers = [
    {
      periodKey: 'pricing.months1', old: 79,
      prices: { USD: 14, EUR: 13, GBP: 12 },
      perKey: 'pricing.per_month1', badge: null, bonusKey: null,
    },
    {
      periodKey: 'pricing.months3', old: 249,
      prices: { USD: 29, EUR: 27, GBP: 25 },
      perKey: 'pricing.per_month3', badge: null, bonusKey: null,
    },
    {
      periodKey: 'pricing.months6', old: 349,
      prices: { USD: 45, EUR: 40, GBP: 35 },
      perKey: 'pricing.per_month6', badge: '-33%', bonusKey: null,
    },
    {
      periodKey: 'pricing.months12', old: 499,
      prices: { USD: 65, EUR: 60, GBP: 50 },
      perKey: 'pricing.per_month12', badge: '-50%', bonusKey: 'pricing.bonus',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto mb-8 sm:mb-10">
      <div className="rounded-3xl border border-[#862ce0]/25 bg-gradient-to-b from-gray-900/95 to-gray-950 p-5 sm:p-8 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[350px] h-[220px] bg-[#862ce0]/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[200px] h-[200px] bg-[#c026d3]/8 rounded-full blur-[80px] pointer-events-none" />

        <div className="text-center mb-6 relative z-10">
          <span className="inline-block bg-[#862ce0]/10 text-[#b47ef3] border border-[#862ce0]/35 text-xs px-5 py-1.5 rounded-full mb-3 font-bold tracking-wide">
            {t('pricing.premium_badge')}
          </span>
          <h3 className="text-2xl sm:text-3xl font-black text-white mb-1">{t('pricing.premium_title')}</h3>
          <p className="text-xs sm:text-sm text-gray-500">{t('pricing.premium_sub')}</p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5 relative z-10">
          {premiumStats.map(({ valKey, subKey, icon }) => (
            <div key={subKey} className="text-center bg-gray-800/60 border border-[#862ce0]/15 rounded-xl p-2.5 sm:p-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-lg sm:rounded-xl bg-gradient-to-br from-[#862ce0]/25 to-[#c026d3]/15 flex items-center justify-center mb-1.5">
                <i className={`${icon} text-[#b47ef3]`} />
              </div>
              <p className="text-xs sm:text-base font-black text-white">{t(valKey)}</p>
              <p className="text-[9px] sm:text-xs text-gray-500">{t(subKey)}</p>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:gap-x-4 sm:gap-y-1.5 mb-5 relative z-10 bg-gray-800/30 rounded-xl p-3 sm:p-4 border border-white/5">
          {premiumFeatures.map(key => (
            <div key={key} className="flex items-center gap-1">
              <i className="ri-check-line text-[#b47ef3] text-xs flex-shrink-0" />
              <span className="text-[10px] sm:text-sm text-gray-300 leading-tight">{t(key)}</span>
            </div>
          ))}
        </div>

        {/* Pricing tiers - 4 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 relative z-10">
          {tiers.map(({ periodKey, old, prices, perKey, badge, bonusKey }) => {
            const price = prices[currency.code as CurrencyKey] ?? prices.USD;
            return (
              <div key={periodKey} className="relative rounded-2xl p-[1px] bg-gradient-to-b from-[#862ce0]/30 to-[#862ce0]/8 hover:scale-[1.02] transition-all duration-300">
                {badge && (
                  <div className="absolute -top-2.5 left-0 right-0 flex justify-center z-10">
                    <span className="bg-gradient-to-r from-[#862ce0] to-[#c026d3] text-white text-[11px] px-3 py-0.5 rounded-full shadow-lg font-bold">{badge}</span>
                  </div>
                )}
                <div className="rounded-2xl p-2.5 sm:p-4 h-full flex flex-col items-center text-center bg-gray-900/95">
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2 text-[#b47ef3]">{t(periodKey)}</p>
                  <span className="text-gray-500 text-[10px] sm:text-xs line-through">{old} {currency.code}</span>
                  <div className="flex items-baseline my-0.5 sm:my-1">
                    <span className="text-2xl sm:text-4xl font-black text-white tracking-tight">{formatPrice(price)}</span>
                    <span className="text-gray-400 text-[10px] sm:text-sm ml-0.5 font-medium">{currency.code}</span>
                  </div>
                  <p className="text-[9px] sm:text-[11px] text-[#b47ef3]/70 mb-2 sm:mb-3">{t(perKey)}</p>
                  {bonusKey && (
                    <div className="w-full mb-2 p-1.5 sm:p-2 rounded-lg bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20">
                      <p className="text-[9px] sm:text-[11px] font-bold text-emerald-400">{t(bonusKey)}</p>
                    </div>
                  )}
                  <a
                    href="https://wa.me/12057092376"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 w-full h-8 sm:h-10 rounded-lg sm:rounded-xl font-bold text-[10px] sm:text-sm bg-[#862ce0]/15 hover:bg-[#862ce0]/30 text-[#b47ef3] border border-[#862ce0]/35 transition-all duration-300 mt-auto cursor-pointer flex items-center justify-center whitespace-nowrap"
                  >
                    {t('pricing.btn_test')}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function GoldPlan() {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const goldStats = [
    { valKey: 'pricing.gs1_val', subKey: 'pricing.gs1_sub', icon: 'ri-tv-2-line' },
    { valKey: 'pricing.gs2_val', subKey: 'pricing.gs2_sub', icon: 'ri-film-line' },
    { valKey: 'pricing.gs3_val', subKey: 'pricing.gs3_sub', icon: 'ri-apps-2-line' },
    { valKey: 'pricing.gs4_val', subKey: 'pricing.gs4_sub', icon: 'ri-shield-flash-line' },
  ];

  const goldFeatures = [
    'pricing.gf1', 'pricing.gf2', 'pricing.gf3', 'pricing.gf4',
    'pricing.gf5', 'pricing.gf6', 'pricing.gf7', 'pricing.gf8',
    'pricing.gf9', 'pricing.gf10', 'pricing.gf11', 'pricing.gf12',
  ];

  type CurrencyKey = 'USD' | 'EUR' | 'GBP';
  const formatPrice = (n: number) => (n % 1 === 0 ? n.toString() : n.toFixed(2));

  const tiers = [
    {
      periodKey: 'pricing.months1', old: 149,
      prices: { USD: 17, EUR: 15, GBP: 13 },
      perKey: 'pricing.per_month1g', badge: null, top: false,
    },
    {
      periodKey: 'pricing.months3', old: 349,
      prices: { USD: 35, EUR: 33, GBP: 29 },
      perKey: 'pricing.per_month3g', badge: null, top: false,
    },
    {
      periodKey: 'pricing.months6', old: 449,
      prices: { USD: 55, EUR: 50, GBP: 45 },
      perKey: 'pricing.per_month6g', badge: '-33%', top: false,
    },
    {
      periodKey: 'pricing.months12', old: 699,
      prices: { USD: 78, EUR: 70, GBP: 65 },
      perKey: 'pricing.per_month12g', badge: '-50%', top: true,
    },
  ];

  return (
    <div className="max-w-5xl mx-auto mb-8 sm:mb-12">
      <div className="rounded-3xl border-2 border-amber-500/30 bg-gradient-to-b from-gray-900 via-gray-950 to-gray-900 p-5 sm:p-8 pt-8 sm:pt-10 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[250px] h-[250px] bg-amber-500/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[200px] h-[200px] bg-orange-500/5 rounded-full blur-[80px] pointer-events-none" />

        {/* Recommended badge */}
        <div className="flex justify-center mb-4 relative z-20">
          <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs px-5 py-1.5 rounded-full shadow-xl shadow-amber-500/30 font-bold">
            {t('pricing.recommended')}
          </span>
        </div>

        <div className="text-center mb-6 relative z-10">
          <span className="inline-block bg-amber-500/10 text-amber-400 border border-amber-500/30 text-xs px-5 py-1.5 rounded-full mb-3 font-bold tracking-wide">
            {t('pricing.gold_badge')}
          </span>
          <h3 className="text-2xl sm:text-3xl font-black text-white mb-1">
            {t('pricing.gold_title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
              {t('pricing.gold_title2')}
            </span>
          </h3>
          <p className="text-xs sm:text-sm text-gray-400 mb-2">{t('pricing.gold_sub')}</p>
          <p
            className="text-sm sm:text-base text-amber-300/90 font-medium max-w-xl mx-auto leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t('pricing.gold_desc1') }}
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5 relative z-10">
          {goldStats.map(({ valKey, subKey, icon }) => (
            <div key={subKey} className="text-center bg-gray-800/60 border border-amber-500/10 rounded-xl p-2.5 sm:p-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/10 flex items-center justify-center mb-1.5">
                <i className={`${icon} text-amber-400`} />
              </div>
              <p className="text-xs sm:text-base font-black text-white">{t(valKey)}</p>
              <p className="text-[9px] sm:text-xs text-gray-500">{t(subKey)}</p>
            </div>
          ))}
        </div>

        {/* Gold features */}
        <div className="mb-5 relative z-10 bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-xl p-3 sm:p-4 border border-amber-500/15">
          <p className="text-[11px] sm:text-xs font-bold text-amber-400 mb-2 sm:mb-3 flex items-center gap-1.5">
            <i className="ri-star-line" /> {t('pricing.gold_badge')}
          </p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:gap-x-4 sm:gap-y-1.5">
            {goldFeatures.map(key => (
              <div key={key} className="flex items-center gap-1">
                <i className="ri-check-line text-amber-400 text-xs flex-shrink-0" />
                <span className="text-[10px] sm:text-sm text-gray-300 leading-tight">{t(key)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison badge */}
        <div className="mb-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-gray-800/60 rounded-full px-4 py-2 border border-white/5">
            <span className="text-[11px] sm:text-xs text-gray-400">{t('pricing.premium_compare')}</span>
            <span className="text-amber-400 text-sm">→</span>
            <span className="text-[11px] sm:text-xs font-bold text-amber-400">{t('pricing.gold_compare')}</span>
          </div>
        </div>

        {/* Pricing tiers - 4 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 relative z-10">
          {tiers.map(({ periodKey, old, prices, perKey, badge, top }) => {
            const price = prices[currency.code as CurrencyKey] ?? prices.USD;
            return (
              <div
                key={periodKey}
                className={`relative rounded-2xl transition-all duration-300 hover:scale-[1.02] ${
                  top
                    ? 'p-[2px] bg-gradient-to-b from-amber-400 via-orange-400 to-amber-600 shadow-2xl shadow-amber-500/20'
                    : 'p-[1px] bg-gradient-to-b from-amber-500/25 to-amber-500/5'
                }`}
              >
                {badge && (
                  <div className="absolute -top-2.5 left-0 right-0 flex justify-center gap-2 z-10">
                    <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[11px] px-3 py-0.5 rounded-full shadow-lg font-bold">{badge}</span>
                    {top && <span className="bg-gray-900 text-white text-[11px] px-3 py-0.5 rounded-full shadow-lg border border-amber-500/50">N°1</span>}
                  </div>
                )}
                <div className={`rounded-2xl p-2.5 sm:p-4 h-full flex flex-col items-center text-center ${top ? 'bg-gray-950' : 'bg-gray-900/95'}`}>
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2 text-amber-400">{t(periodKey)}</p>
                  <span className="text-gray-500 text-[10px] sm:text-xs line-through">{old} {currency.code}</span>
                  <div className="flex items-baseline my-0.5 sm:my-1">
                    <span className="text-2xl sm:text-4xl font-black text-white tracking-tight">{formatPrice(price)}</span>
                    <span className="text-gray-400 text-[10px] sm:text-sm ml-0.5 font-medium">{currency.code}</span>
                  </div>
                  <p className="text-[9px] sm:text-[11px] text-amber-400/70 mb-1.5 sm:mb-2">{t(perKey)}</p>
                  <span className="inline-block bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[8px] sm:text-[10px] px-1.5 sm:px-2.5 py-0.5 rounded-full mb-2 sm:mb-3 font-medium">
                    {t('pricing.replay')}
                  </span>
                  <a
                    href="https://wa.me/12057092376"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-3 py-2 w-full h-8 sm:h-10 rounded-lg sm:rounded-xl font-bold text-[9px] sm:text-sm transition-all duration-300 mt-auto flex items-center justify-center whitespace-nowrap cursor-pointer ${
                      top
                        ? 'bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90 text-white shadow-lg shadow-amber-500/20'
                        : 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30'
                    }`}
                  >
                    {top ? t('pricing.btn_choose') : t('pricing.btn_test')}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function PricingSection() {
  const { t } = useTranslation();

  return (
    <section id="pricing" className="py-10 sm:py-16 md:py-20 bg-gray-950 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-[#862ce0]/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block bg-white/5 text-gray-300 border border-white/10 text-xs px-4 py-1 rounded-full mb-4 font-medium">
            {t('pricing.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2 tracking-tight">
            {t('pricing.title')}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 max-w-xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        <PremiumPlan />

        {/* Separator */}
        <div className="max-w-5xl mx-auto mb-8 sm:mb-10">
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-white/10" />
            <span className="text-gray-600 text-[10px] sm:text-xs font-medium uppercase tracking-widest whitespace-nowrap">⬆ {t('pricing.gold_badge')}</span>
            <div className="flex-1 border-t border-white/10" />
          </div>
        </div>

        <GoldPlan />

      </div>
    </section>
  );
}
